import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldStar, EnvelopeSimple, CheckCircle, Warning, LockKey } from "@phosphor-icons/react";

/** /auth/forgot — user enters email, we send a reset link via Resend. */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Reset your password" testid="forgot-page">
      {!sent ? (
        <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
          <p className="text-[13px] text-[var(--span-text-2)]">
            Enter your SPAN email. If we find an account, we&apos;ll send you a link to set a new password.
          </p>
          <div>
            <label className="text-[11px] text-[var(--span-text-2)] block mb-1">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              placeholder="you@spanip.com"
              className="span-input font-mono text-[13px] w-full"
            />
          </div>
          {err && <div className="text-[12px] text-[var(--span-danger)]" data-testid="forgot-error">{err}</div>}
          <button
            type="submit"
            disabled={busy || !email}
            data-testid="forgot-submit"
            className="span-btn w-full disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
            <EnvelopeSimple size={13} /> {busy ? "Sending…" : "Send reset link"}
          </button>
        </form>
      ) : (
        <div className="text-[13px] space-y-3" data-testid="forgot-sent">
          <div className="flex items-start gap-2 text-emerald-700">
            <CheckCircle size={18} weight="duotone" className="mt-0.5" />
            <div>
              <div className="font-semibold">Check your inbox</div>
              <div className="text-[var(--span-text-2)]">
                If <strong>{email}</strong> is a valid SPAN account, you&apos;ll receive an email in the next minute or two with a link to set a new password. The link expires in 60 minutes.
              </div>
            </div>
          </div>
          <div className="text-[11px] text-[var(--span-text-2)] pt-2 border-t border-[var(--span-border)]">
            Didn&apos;t receive anything after 5 minutes? Check your spam folder, then contact your Reporting Manager.
          </div>
        </div>
      )}
      <div className="text-center pt-3">
        <Link to="/login" data-testid="back-to-login"
              className="text-[12px] text-[var(--span-text-2)] hover:text-[var(--brand-primary)] hover:underline">
          ← Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}

/** /auth/reset?token=xxx — user sets new password using the token from email. */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState({ state: "checking" });
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setStatus({ state: "bad", message: "Missing token." }); return; }
    api.get(`/auth/reset-token/${encodeURIComponent(token)}`)
      .then(r => setStatus({ state: "ok", ...r.data }))
      .catch(e => setStatus({
        state: "bad",
        message: e?.response?.data?.detail || "This reset link is invalid or expired.",
      }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw1 !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      alert("Password updated. Please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Reset failed.");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Set a new password" testid="reset-page">
      {status.state === "checking" && (
        <div className="text-[13px] text-[var(--span-text-2)]">Validating your reset link…</div>
      )}
      {status.state === "bad" && (
        <div className="space-y-3" data-testid="reset-bad">
          <div className="flex items-start gap-2 text-rose-700">
            <Warning size={18} weight="duotone" className="mt-0.5" />
            <div>
              <div className="font-semibold">Link no longer valid</div>
              <div className="text-[var(--span-text-2)] text-[12px]">{status.message}</div>
            </div>
          </div>
          <Link to="/auth/forgot" data-testid="reset-request-new"
                className="span-btn w-full inline-flex items-center justify-center">
            Request a new reset link
          </Link>
        </div>
      )}
      {status.state === "ok" && (
        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
          <div className="flex items-center gap-2 text-[12px] text-[var(--span-text-2)] pb-1 border-b border-[var(--span-border)]">
            <LockKey size={14} />
            Setting new password for <strong>{status.email}</strong>
          </div>
          <div>
            <label className="text-[11px] text-[var(--span-text-2)] block mb-1">New password (min 8 chars)</label>
            <input type="password" required minLength={8} autoFocus
              value={pw1} onChange={(e) => setPw1(e.target.value)}
              data-testid="reset-new-pw"
              className="span-input font-mono text-[13px] w-full" />
          </div>
          <div>
            <label className="text-[11px] text-[var(--span-text-2)] block mb-1">Confirm new password</label>
            <input type="password" required minLength={8}
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              data-testid="reset-new-pw-confirm"
              className="span-input font-mono text-[13px] w-full" />
          </div>
          {err && <div className="text-[12px] text-[var(--span-danger)]" data-testid="reset-error">{err}</div>}
          <button type="submit" disabled={busy || !pw1 || !pw2}
            data-testid="reset-submit"
            className="span-btn w-full disabled:opacity-50">
            {busy ? "Setting password…" : "Set password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

function AuthShell({ title, testid, children }) {
  return (
    <div data-testid={testid}
      className="min-h-screen bg-[var(--span-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-[var(--span-surface)] border border-[var(--span-border)] rounded-lg shadow-2xl overflow-hidden">
        <div className="bg-[var(--brand-primary,#263776)] text-white p-5">
          <div className="flex items-center gap-2">
            <ShieldStar size={22} weight="duotone" />
            <div>
              <div className="text-[16px] font-bold">SPAN CasePrime</div>
              <div className="text-[10px] tracking-[0.2em] uppercase opacity-80">{title}</div>
            </div>
          </div>
        </div>
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>
  );
}
